import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  InputBase,
  Popper,
  Paper,
  List,
  ListSubheader,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Chip,
  ClickAwayListener,
  alpha,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';

const DEBOUNCE_MS = 300;

const GROUPS = [
  { key: 'detections', label: 'Detections', path: (d) => `/dashboard/detections/${d._id}`, primary: (d) => d.title, secondary: (d) => [d.severity, d.status].filter(Boolean).join(' · ') },
  { key: 'cases', label: 'Cases', path: (c) => `/dashboard/cases/${c._id}`, primary: (c) => `${c.caseNumber ? `${c.caseNumber} — ` : ''}${c.title}`, secondary: (c) => [c.severity, c.status].filter(Boolean).join(' · ') },
  { key: 'indicators', label: 'Indicators', path: (i) => `/dashboard/indicators/${i._id}`, primary: (i) => i.value, secondary: (i) => i.type },
  { key: 'entities', label: 'Entities', path: (e) => `/dashboard/entities/${e._id}`, primary: (e) => e.name, secondary: (e) => [e.type, e.identifier].filter(Boolean).join(' · ') },
  { key: 'users', label: 'Users', path: () => '/dashboard/settings', primary: (u) => u.name, secondary: (u) => [u.email, u.role].filter(Boolean).join(' · ') },
];

const severityColor = (s) => ({ Critical: 'error', High: 'warning', Medium: 'info', Low: 'default' }[s] || 'default');

const GlobalSearch = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const requestId = useRef(0);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  // Flatten results into a single navigable list
  const flat = useMemo(() => {
    if (!results) return [];
    const out = [];
    GROUPS.forEach((g) => {
      (results[g.key] || []).forEach((item) => out.push({ group: g, item, path: g.path(item) }));
    });
    return out;
  }, [results]);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      try {
        const res = await apiService.search(q);
        if (id !== requestId.current) return;
        setResults(res.data);
        setActive(0);
        setOpen(true);
      } catch (e) {
        if (id === requestId.current) setResults({ error: true });
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // "/" focuses the search box unless typing in another field
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      e.preventDefault();
      if (inputRef.current) inputRef.current.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const select = useCallback((entry) => {
    if (!entry) return;
    setOpen(false);
    setQuery('');
    setResults(null);
    if (inputRef.current) inputRef.current.blur();
    navigate(entry.path);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      if (inputRef.current) inputRef.current.blur();
      return;
    }
    if (!flat.length) {
      if (e.key === 'Enter') e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(flat[Math.min(active, flat.length - 1)] || flat[0]);
    }
  };

  const total = flat.length;
  const showPopper = open && query.trim().length > 0;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative', mr: 1, display: { xs: 'none', sm: 'block' } }}>
        <Box
          ref={anchorRef}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            bgcolor: alpha(theme.palette.common.white, 0.15),
            '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.25) },
            '&:focus-within': { bgcolor: alpha(theme.palette.common.white, 0.25) },
            px: 1,
            width: { sm: 220, md: 320 },
            transition: theme.transitions.create('width'),
          })}
        >
          <SearchIcon sx={{ color: 'inherit', opacity: 0.8, mr: 1 }} fontSize="small" />
          <InputBase
            inputRef={inputRef}
            value={query}
            placeholder="Search…  ( / )"
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => { if (results) setOpen(true); }}
            onKeyDown={handleKeyDown}
            inputProps={{ 'aria-label': 'global search', autoComplete: 'off' }}
            sx={{ color: 'inherit', flex: 1, fontSize: 14, py: 0.5, '& ::placeholder': { opacity: 0.8 } }}
          />
          {loading && <CircularProgress size={14} color="inherit" sx={{ ml: 1 }} />}
        </Box>

        <Popper open={showPopper} anchorEl={anchorRef.current} placement="bottom-end" sx={{ zIndex: (t) => t.zIndex.modal + 1 }}>
          <Paper elevation={6} sx={{ width: 420, maxWidth: '95vw', maxHeight: 480, overflowY: 'auto', mt: 0.5 }}>
            {results && results.error ? (
              <Box sx={{ p: 2 }}><Typography variant="body2" color="error">Search failed. Try again.</Typography></Box>
            ) : !results || loading ? (
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Searching…</Typography>
              </Box>
            ) : total === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">No results for “{query.trim()}”.</Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {GROUPS.map((g) => {
                  const items = results[g.key] || [];
                  if (!items.length) return null;
                  return (
                    <li key={g.key}>
                      <ul style={{ padding: 0 }}>
                        <ListSubheader disableSticky sx={{ lineHeight: '32px', bgcolor: 'background.paper' }}>
                          {g.label} ({items.length})
                        </ListSubheader>
                        {items.map((item) => {
                          const index = flat.findIndex((f) => f.item === item);
                          const isActive = index === active;
                          return (
                            <ListItemButton
                              key={item._id}
                              selected={isActive}
                              onMouseEnter={() => setActive(index)}
                              onClick={() => select(flat[index])}
                            >
                              <ListItemText
                                primary={g.primary(item)}
                                secondary={g.secondary(item)}
                                primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                                secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                              />
                              {item.severity && (
                                <Chip label={item.severity} size="small" color={severityColor(item.severity)} sx={{ ml: 1 }} />
                              )}
                            </ListItemButton>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </List>
            )}
            {total > 0 && (
              <Box sx={{ px: 2, py: 0.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">↑↓ to navigate · Enter to open · Esc to close</Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
